// THE LOBBY'S TABLES — pure maths, no DOM, no React, and NO ENGINE.
//
// ── WHAT THIS IS FOR ─────────────────────────────────────────────────────
// Two things gave the game away the moment a player opened it, and the owner
// named both:
//
//   "as soon as the user enters, it always sees the 20 second timer start at
//    that very moment — how come our ongoing game could always start at that
//    moment when the user enters"
//   "there are no older records whenever the user enters for the first time,
//    which is making it highly suspicious"
//
// Both are the same tell: the table did not exist until you looked at it. A
// real table has been running all day; you arrive somewhere in the middle of a
// round, and it already has a history.
//
// ── HOW IT IS SOLVED ─────────────────────────────────────────────────────
// Every table gets a VIRTUAL CLOCK. Its round index is a function of the wall
// clock and the table's own phase offset, so the table advances whether or not
// anyone is looking at it. Its results are a function of (table seed, round
// index) — so they are consistent, reproducible, and already there before the
// player has done anything.
//
// NOTHING IS STORED except a single device seed. A table's entire past is
// derived, which means it cannot drift, cannot be tampered with by clearing
// storage, and costs nothing. Leave a table for five minutes and come back: it
// has eleven more results, because eleven more rounds actually elapsed.
//
// ── WHY EVERY PHONE SEES DIFFERENT TABLES ────────────────────────────────
// The owner: "every single phone will have 8 to 10 random IDs shown, due to
// which no ID will match with each other no matter two players are sitting
// together." The device seed is drawn once, at random, and kept. Every table
// id, name, phase and result stream descends from it, so two phones side by
// side share nothing — and the same phone sees the same tables tomorrow, which
// matters just as much: a lobby whose tables change identity on every visit is
// its own tell.
//
// This file imports nothing. It is unit-tested by
// scripts/assets/roulette_lobby_test.mjs.

/* ── HOW LONG A ROUND TAKES ──────────────────────────────────────────────
 * Defined HERE and imported by Page.tsx, so the virtual clock a table keeps in
 * the lobby and the real clock it runs on once you are sitting at it are the
 * same number. They were two constants before; a quick table would have shown a
 * history that did not match the pace it actually played at.
 *
 * QUICK IS A PROPERTY OF THE ROOM, not a switch inside the game. The owner:
 * "they should be sessions where the user gets to know that this particular
 * session is a quick one... when the user gets inside, it should not be able to
 * switch it off." So a quick room bets for twelve seconds instead of twenty and
 * spins faster, and that is decided in the lobby, once. */
export const WINDOW_MS = 20_000;         // betting window at a normal table
export const QUICK_WINDOW_MS = 12_000;   // and at a quick one
export const RESULT_MS = 7_000;          // spin, reveal, clear down

/** The betting window for this table. */
export const windowMsFor = (t: Pick<Table, 'fast'>) =>
  (t.fast ? QUICK_WINDOW_MS : WINDOW_MS);

/** One whole round, betting plus result. */
export const roundMsFor = (t: Pick<Table, 'fast'>) => windowMsFor(t) + RESULT_MS;

/** A normal table's round, for callers that have no table in hand. */
export const ROUND_MS = WINDOW_MS + RESULT_MS;

/** A fixed instant in the past that all virtual clocks count from. Any constant
 *  does; it only has to never change, or every table's history would jump. */
const EPOCH = 1_735_689_600_000;   // 2025-01-01T00:00:00Z

const KEY = 'rlt_lobby_seed';

/* ─── the seed ────────────────────────────────────────────────────────────
 * 32 bits, drawn once per device and kept. Everything below is a pure function
 * of this number, so it is the only state the lobby has.
 */
export function deviceSeed(): number {
  try {
    const v = localStorage.getItem(KEY);
    if (v) {
      const n = parseInt(v, 36);
      if (Number.isFinite(n) && n > 0) return n;
    }
  } catch { /* private mode — fall through and use a volatile seed */ }
  const s = (Math.floor(Math.random() * 0xffffffff) >>> 0) || 0x9e3779b9;
  try { localStorage.setItem(KEY, s.toString(36)); } catch { /* ignore */ }
  return s;
}

/** A small, fast, well-mixed integer hash. Not cryptographic and does not need
 *  to be — it only has to make (seed, round) look nothing like (seed, round+1),
 *  which xorshift-multiply does perfectly well. */
export function hash(a: number, b: number): number {
  let h = (a ^ 0x9e3779b9) >>> 0;
  h = Math.imul(h ^ (b + 0x165667b1), 0x27d4eb2d) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x85ebca6b) >>> 0;
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** hash(), mapped to [0, 1). */
const frac = (a: number, b: number) => hash(a, b) / 0x100000000;

/* ─── the rooms ───────────────────────────────────────────────────────────
 * Names, not just numbers. A lobby of ten rows that differ only by a hex id is
 * a spreadsheet; a lobby of rooms with names is a casino. The pool is larger
 * than the lobby so two devices rarely draw the same set.
 */
const ROOMS = [
  'Ruby Room', 'Emerald Hall', 'Sapphire Suite', 'Golden Gate', 'Royal Court',
  'Diamond Deck', 'Velvet Lounge', 'Monsoon Room', 'Crown Parlour', 'Ivory Salon',
  'Midnight Club', 'Marigold Hall', 'Peacock Room', 'Sunset Terrace', 'Grand Bazaar',
] as const;

export type Table = {
  /** shown to the player, e.g. "7KQ4-2M" */
  id: string;
  name: string;
  /** the table's own offset into the round cycle, so no two tick together */
  phase: number;
  /** minimum chip this room accepts */
  minBet: number;
  /** a couple of rooms run quicker, purely for texture */
  fast: boolean;
  seatBase: number;
  seed: number;
};

const B32 = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';   // no 0/O/1/I — read aloud safely

function idFrom(seed: number): string {
  let s = '';
  for (let i = 0; i < 6; i++) s += B32[hash(seed, i + 101) % B32.length];
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

/**
 * The device's lobby. Nine tables — enough that the list feels like a room and
 * short enough to read without scrolling far.
 */
export function buildTables(seed: number, count = 9): Table[] {
  const rooms = [...ROOMS];
  // Fisher-Yates against the device seed, so the NAMES differ per phone too and
  // not only the ids.
  for (let i = rooms.length - 1; i > 0; i--) {
    const j = hash(seed, 900 + i) % (i + 1);
    [rooms[i], rooms[j]] = [rooms[j], rooms[i]];
  }
  const out: Table[] = [];
  for (let i = 0; i < count; i++) {
    const s = hash(seed, i * 7919 + 13);
    out.push({
      seed: s,
      id: idFrom(s),
      name: rooms[i % rooms.length],
      // spread around the cycle so the lobby always shows a range of
      // countdowns — every table reading the same number would be the tell
      phase: Math.floor(frac(s, 3) * ROUND_MS),
      minBet: [10, 10, 10, 50, 50, 100][hash(s, 5) % 6],
      fast: hash(s, 9) % 5 === 0,
      seatBase: 40 + (hash(s, 11) % 620),
    });
  }
  return out;
}

/* ─── the virtual clock ───────────────────────────────────────────────────── */

const period = (t: Table) => roundMsFor(t);

/** Which round this table is on at instant `now`. Advances on its own. */
export function roundIndex(t: Table, now: number): number {
  return Math.floor((now - EPOCH + t.phase) / period(t));
}

/** Milliseconds until this table's current round ends. */
export function msToNextRound(t: Table, now: number): number {
  const p = period(t);
  return p - ((now - EPOCH + t.phase) % p);
}

/** The result of one specific round. 0..36, uniform. */
export function resultAt(t: Table, round: number): number {
  return overlayAt(t, round) ?? hash(t.seed, round) % 37;
}

/* ── THE RESULT OVERLAY — what the player actually watched, kept ──────────
 *
 * The derived history is a fiction the table tells about its past, and it is a
 * good one right up until the player has SEEN a round: the game's live results
 * are drawn at spin time, so on rejoining a table (or reading its lobby card)
 * the Record used to show the hash-derived numbers instead of the ones the
 * player just watched — the room contradicting the player's own memory, which
 * is the single fastest way to break the fiction. (2026-08-16 audit, m6/m13.)
 *
 * So every settled round writes its real number here, keyed by the table and
 * the ROUND INDEX it landed in, and resultAt() serves the overlay before the
 * fiction. Lobby cards, the join-time history seed and the in-game Record all
 * flow through resultAt, so all three agree with what was seen — and rounds
 * nobody watched keep their derived numbers, which no one can contradict.
 *
 * localStorage, capped at the newest 24 per table: enough to cover every
 * surface that shows history, small enough to never matter. */
const OVERLAY_KEY = 'rlt_seen';
type Seen = Record<string, { r: number; n: number }[]>;

function readSeen(): Seen {
  /* Shape-validated, not just parse-guarded: JSON.parse('null') SUCCEEDS, and
     a null (or an array, or a per-table value that is not an array of rows)
     would throw later inside overlayAt -- which is called from a useState
     initializer, so one corrupt localStorage value white-screened the whole
     roulette entry with no self-heal. Anything malformed reads as empty and
     the next recordResult overwrites it. (Recheck P7.) */
  try {
    const v = JSON.parse(localStorage.getItem(OVERLAY_KEY) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

function overlayAt(t: Table, round: number): number | null {
  const rows = readSeen()[t.id];
  if (!Array.isArray(rows)) return null;
  const hit = rows.find((x) => x.r === round);
  return hit ? hit.n : null;
}

/** Record a live result against the round it landed in. Call at settle. */
export function recordResult(t: Table, n: number, now: number): void {
  try {
    const seen = readSeen();
    const rows = Array.isArray(seen[t.id]) ? seen[t.id] : [];
    const r = roundIndex(t, now);
    const next = rows.filter((x) => x.r !== r);
    next.push({ r, n });
    next.sort((a, b) => a.r - b.r);
    seen[t.id] = next.slice(-24);
    localStorage.setItem(OVERLAY_KEY, JSON.stringify(seen));
  } catch { /* private mode -- the fiction survives un-overlaid */ }
}

/**
 * The last `n` results, newest first — exactly what the Record column wants.
 *
 * This is the fix for "no older records when the user enters". The player joins
 * a table that already has a past, because the past is a function of a clock
 * that has been running since before they installed the app.
 */
export function recentResults(t: Table, now: number, n = 18): number[] {
  const idx = roundIndex(t, now);
  const out: number[] = [];
  for (let k = 1; k <= n; k++) out.push(resultAt(t, idx - k));
  return out;
}

/** Roughly how many people are at this table. Drifts slowly and reverts, so it
 *  looks watched rather than generated. */
export function seatedAt(t: Table, now: number): number {
  const idx = roundIndex(t, now);
  const slow = Math.sin((idx / 140) + t.phase) * 0.22;
  const jitter = (frac(t.seed, idx) - 0.5) * 0.10;
  return Math.max(6, Math.round(t.seatBase * (1 + slow + jitter)));
}

/* ─── joining ─────────────────────────────────────────────────────────────
 * The owner, revised: "do not make them hold for long — try making them wait for
 * six seconds maximum, or maximum seven to eight seconds. I don't think users
 * will wait." Right, and the first version was far too generous with their
 * patience at 3-18 s.
 *
 * The wait is the tail of the round already in progress — which is the honest
 * answer, and it is also why the number the lobby shows is the number the join
 * screen counts down: they are the same function of the same clock, not two
 * guesses that have to be kept in step.
 *
 * It is COMPRESSED rather than used raw. A true remainder is uniform over the
 * whole cycle, so a third of joins would sit through twenty seconds of nothing,
 * and a lobby that punishes a third of its taps is a lobby people stop tapping.
 * Mapping into 2-6 s keeps the variability — the point is that it is never the
 * same twice, so it reads as a real table rather than a fixed delay — while
 * staying inside the time a player will actually give you. One join in twelve
 * gets an extra two seconds, so 8 s is the ceiling and it is rare.
 */
export function joinWaitMs(t: Table, now: number): number {
  const raw = msToNextRound(t, now);
  const base = 2_000 + (raw % 4_000);                  // 2.0 s .. 6.0 s
  const rare = hash(t.seed, Math.floor(now / 1000)) % 12 === 0 ? 2_000 : 0;
  return base + rare;                                  // never above 8 s
}

/** `7KQ4-2M` → what the player sees in-game, and the reason two phones can
 *  never be confused for the same session. */
export function shortId(t: Table): string {
  return t.id;
}
